import Grid from "@mui/material/Unstable_Grid2";
import { Facebook, LinkedIn, Twitter, WhatsApp } from "@mui/icons-material";
import XIcon from "@mui/icons-material/X";
import { useRouter } from "next/router";
import React, { useEffect, useState } from "react";
import styles from "../../styles/pages/[blogId].module.scss";
import { images } from "../../constants";
import Meta from "../../layouts/Meta";
import StaticContainer from "../../layouts/StaticContainer";
import PreviousAndNext from "../../components/blog/[blogId]/PreviousAndNext";
import BlogPosts from "../../components/_shared/BlogPosts";
import { getBlogDetails, getBlogList } from "../../services/blogs";
import { buildArticleSchema } from "../../lib/articleSchema";
import { Blog } from "../../interfaces";
import ShareThisPost from "../../components/_shared/ShareThisPost";
import RelatedPosts from "../../components/blog/[blogId]/RelatedPosts";
import { Link, Skeleton } from "@mui/material";
import { GetStaticPaths, GetStaticProps } from "next";
import { MdKeyboardBackspace, MdPersonOutline, MdCalendarToday } from "react-icons/md";
import { formatDateMY } from "../../utils/date";

const BlogDetails = (props: Props) => {
  const { blogs, blogIndex, blog } = props;
  const router = useRouter();
  const { blogId }: any = router.query;
  const [socialButtons, setSocialButtons] = useState(null);

  const locale = router.locale || "en";
  const postUrl = `https://sols.foundation${
    locale === "en" ? "" : `/${locale}`
  }${router.asPath.split("?")[0].split("#")[0]}`;
  const articleSchema = blog
    ? buildArticleSchema({
        url: postUrl,
        headline: blog.postTitle,
        description: blog.postMetaDesc || blog.postExcerpt,
        image: blog.postImage,
        datePublished: blog.postDateTs
          ? new Date(Number(blog.postDateTs)).toISOString()
          : undefined,
      })
    : null;

  useEffect(() => {
    const call = async () => {
      if (!blog) router.replace("/404", router.asPath);
      // console.log('blog',blog);

      const domain = window.location.hostname;
      const social_buttons = [
        {
          name: "blog-facebook",
          icon: <Facebook />,
          color: "#4267B2",
          href: `https://www.facebook.com/sharer.php?u=https://${domain}${router.asPath}&t=${blog?.postTitle}`,
        },
        {
          name: "blog-twitter",
          icon: <XIcon />,
          color: "#1DA1F2",
          href: `https://twitter.com/share?url=https://${domain}${router.asPath}&text=${blog?.postTitle}`,
        },
        {
          name: "blog-linkedin",
          icon: <LinkedIn />,
          color: "#0072b1",
          href: `https://www.linkedin.com/shareArticle?mini=true&url=https://${domain}${router.asPath}&title=${blog?.postTitle}&summary=&source=SOLS%2024%2F7`,
        },
        {
          name: "blog-whatsapp",
          icon: <WhatsApp />,
          color: "#128C7E",
          href: `https://api.whatsapp.com/send?text=${blog?.postTitle}%0Ahttps://${domain}${router.asPath}`,
        },
      ];
      setSocialButtons(social_buttons);
    };

    call();
  }, [blog, blogId, router]);

  return (
    <StaticContainer title={blog?.postTitle}>
      <Meta
        title={blog?.postTitle}
        description={blog?.postMetaDesc}
        keywords={blog?.postTitle}
        image={blog?.postImage}
      />
      {articleSchema && (
        <script
          type="application/ld+json"
          // Escape `<` so a "</script>" inside CMS-sourced fields (e.g. a post
          // title) cannot break out of the script tag.
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(articleSchema).replace(/</g, "\\u003c"),
          }}
        />
      )}
      <Grid container spacing={5}>
        {blog ? (
          <Grid xs={12} lg={9} className={styles.main}>
            <div className={styles.gridContainer}>
              <Link href="/blog" className={styles.backLink}>
                <MdKeyboardBackspace className={styles.backLinkIcon} />
                Back to Blog List
              </Link>
            </div>

            <div className={styles.postMeta}>
              <span className={styles.postAuthor}>
                <MdPersonOutline className={styles.postMetaIcon} />
                Published by SOLS Foundation
              </span>
              <span className={styles.postDate}>
                <MdCalendarToday className={styles.postMetaIcon} />
                {formatDateMY(blog.postDateTs)}
              </span>
            </div>

            <img
              src={blog?.postImage || images.dummy}
              alt={blog?.postTitle}
              width="100%"
              height="auto"
              className={styles.featuredImage}
            />

            <div
              className={styles.descHtml}
              dangerouslySetInnerHTML={{ __html: blog.postContent }}
            />

            <ShareThisPost socialButtons={socialButtons} />

            <PreviousAndNext
              blogs={blogs}
              currentIndex={blogIndex}
              uri="/blog"
            />

            <RelatedPosts blogs={blogs} blogId={blog.postId} />
          </Grid>
        ) : (
          <Grid xs={12} lg={9} className={styles.main}>
            <Skeleton
              variant="rectangular"
              width="100%"
              height={600}
              animation="wave"
            />

            <div className={styles.descHtml}>
              <Skeleton variant="text" width="95%" height={20} />
              <Skeleton variant="text" width="90%" height={16} />
              <Skeleton variant="text" width="95%" height={16} />
              <Skeleton variant="text" width="70%" height={16} />
            </div>

            <hr />
          </Grid>
        )}

        <Grid xs={12} lg={3}>
          {blogs ? (
            <BlogPosts
              blogs={blogs.slice(0, 6).filter((blog) => blog.postId !== blogId)}
            />
          ) : null}
        </Grid>
      </Grid>
    </StaticContainer>
  );
};

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const { blogId }: any = params;
  // feed blogs array
  const blogs_json = await getBlogList();
  let blogs = [];
  blogs_json?.forEach((blog) => {
    blogs.push(blog.val());
  });
  blogs = blogs.reverse();

  // set index
  const blogIndex = blogs.findIndex((blog) => blog.postId === blogId);

  // get blog details
  const blog_details = await getBlogDetails(blogId);

  // A slug with no record behind it must 404. Returning a null blog instead
  // rendered an empty page on a 200, so every mistyped, renamed or legacy
  // blog URL became a soft 404 that crawlers were free to index.
  if (!blog_details?.exists()) {
    return { notFound: true, revalidate: 1 };
  }

  const blog = { ...(await blogs[blogIndex]), ...(await blog_details.val()) };

  return {
    props: { blogs, blogIndex, blog },
    revalidate: 1,
  };
};

export const getStaticPaths: GetStaticPaths = async ({ locales }) => {
  const blogs_json = await getBlogList();
  let blogs: Blog[] = [];
  let paths: any[] = [];

  blogs_json?.forEach((blog) => {
    blogs.push(blog.val());
  });
  locales?.forEach((locale) => {
    blogs?.forEach((blog) =>
      paths.push({ params: { blogId: blog.postId }, locale })
    );
  });

  // "blocking", not true: with fallback: true Next answers an unknown slug
  // with a 200 shell before getStaticProps has run, so the notFound above
  // would only take effect on the second request. Blocking waits for the
  // data, which means a real 404 on the first hit. New posts still appear
  // without a rebuild; they just render server-side the first time.
  return { paths, fallback: "blocking" };
};

interface Props {
  blogs: Blog[];
  blogIndex: number;
  blog: Blog;
}

export default BlogDetails;
